import {
  Goal,
  GoalDeadlineRowsMap,
  GoalProgressCount,
  GoalTreeIndex,
  ID,
  LifeArea,
  TaskFilterOptions,
} from '@griply/types';
import {
  buildGoalDeadlineRows,
  DeadlineGoal,
  filterGoalsByFilterOptions,
  getDeadlineGoals,
} from '@griply/utils';
import { useMemo } from 'react';

const emptyGoals: DeadlineGoal[] = [];

export type UseGoalDeadlineRowsOptions = {
  goals: Goal[];
  lifeAreas: LifeArea[];
  filters: TaskFilterOptions[];
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  goalTreeIndex?: GoalTreeIndex;
  showCompleted?: boolean;
  enabled?: boolean;
};

export type GoalDeadlineRows = {
  deadlineGoals: DeadlineGoal[];
  goalRows: GoalDeadlineRowsMap;
};

export const useGoalDeadlineRows = ({
  goals,
  lifeAreas,
  filters,
  goalProgressCounts,
  goalTreeIndex,
  showCompleted,
  enabled,
}: UseGoalDeadlineRowsOptions): GoalDeadlineRows => {
  const deadlineGoals = useMemo(
    () =>
      enabled
        ? filterGoalsByFilterOptions(
            // getDeadlineGoals walks ancestors, so it must see every goal.
            getDeadlineGoals(goals, { showCompleted }),
            filters,
          )
        : emptyGoals,
    [enabled, goals, filters, showCompleted],
  );

  // Per goal, never per rendered row.
  const goalRows = useMemo(
    () =>
      buildGoalDeadlineRows(deadlineGoals, {
        goals,
        lifeAreas,
        goalProgressCounts,
        goalTreeIndex,
      }),
    [deadlineGoals, goals, lifeAreas, goalProgressCounts, goalTreeIndex],
  );

  return { deadlineGoals, goalRows };
};
